import { QueryClient, QueryFunctionContext, useQuery, UseQueryOptions, UseMutationOptions, DefaultOptions } from '@tanstack/react-query';
import { AxiosRequestConfig } from 'axios';

import httpService from '@/lib/http.service';

type QueryKeyT = [string, Record<string, any> | undefined];

export const queryConfig = {
  queries: {
    refetchOnWindowFocus: false,
    retry: false,
    staleTime: Infinity,
    refetchOnReconnect: false,
    retryOnMount: false,
  },
} satisfies DefaultOptions;

export const queryClient = new QueryClient({
  defaultOptions: queryConfig,
});

export const fetchFn = async <T>(url: string, config?: AxiosRequestConfig): Promise<T> => {
  return httpService.get<T>(url, config).then((res) => res.data);
};

export const mutationFn = async <ParamsType, ReturnType>(url: string, data?: ParamsType, method: 'patch' | 'post' | 'delete' = 'post') => {
  switch (method) {
    case 'patch': {
      return httpService.patch<ReturnType>(url, data).then((res) => res.data);
    }

    case 'delete': {
      return httpService.delete<ReturnType>(url, data).then((res) => res.data);
    }

    default: {
      return httpService.post<ReturnType>(url, data).then((res) => res.data);
    }
  }
};

export type ApiFnReturnType<FnType extends (...args: any) => Promise<any>> = Awaited<ReturnType<FnType>>;

export type QueryConfig<T extends (...args: any[]) => any> = Omit<ReturnType<T>, 'queryKey' | 'queryFn'>;

export type MutationConfig<MutationFnType extends (...args: any) => Promise<any>> = UseMutationOptions<
  ApiFnReturnType<MutationFnType>,
  Error,
  Parameters<MutationFnType>[0] | void
>;

// TODO: Remove as this does not allow more than 2 keys
export const useFetch = <T>(
  url: string | null,
  params?: Record<string, any>,
  config?: Partial<UseQueryOptions<T, Error, T, QueryKeyT>>,
) => {
  return useQuery<T, Error, T, QueryKeyT>({
    queryKey: [url!, params],
    queryFn: async (context: QueryFunctionContext<QueryKeyT>) =>
      fetchFn(context.queryKey[0], {
        params: context.queryKey[1],
      }),
    enabled: !!url,
    ...config,
  });
};
